<template>
  <mf-card-container class="mf-card-container-margin-top-24">
    <div data-userflow-id="step2">
      <span class="span-title-modelo-aplicacao">Modelo de aplicação</span>
      <div class="div-flex-space-between">
        <div :class="segmentedClass" @click="!disableFieldsAlternative && $emit('update:segmented', true)">
          <div class="div-flex-row-mb-empty">
            <v-icon size="26px">wallet</v-icon>
            <span class="span-com-segmento-title">Com segmento</span>
          </div>
          <span class="span-com-segmento-desc">
            Modelo em que os cupons aparecem diretamente na carteira dos consumidores que fazem parte do segmento, ele não possui um código de cupom.
          </span>
        </div>

        <div :class="nonSegmentedClass" @click="handleNonSegmentedClick">
          <div class="div-flex-row">
            <v-icon size="26px">confirmation_number</v-icon>
            <span class="span-sem-segmento-title">Sem segmento</span>
          </div>

          <template v-if="$getAllowedModules()['mobile']">
            <span class="span-sem-segmento-desc">
              Modelo de aplicação pelo código, qualquer consumidor com o código pode utilizar o cupom e eles possuem um número limitado.
            </span>
          </template>

          <template v-else>
            <mf-tooltip top max-width="300px">
              <template #activator>
                <div class="div-flex-row-lock">
                  <v-icon size="26px">mdi-lock</v-icon>
                  <span class="span-lock-info"> Possibilidade disponível apenas para quem possui o módulo app </span>
                </div>
              </template>
              <span> Fale com o seu consultor para contratar o aplicativo e usar essa opção </span>
            </mf-tooltip>
          </template>
        </div>
      </div>
    </div>

    <template v-if="segmented">
      <div class="div-segmented-block">
        <div class="div-publico-alvo-step3" data-userflow-id="step3">
          <span class="span-publico-alvo-title">Público-alvo</span>
          <publico-alvo
            :single-select="true"
            :segmentos.sync="localSegments"
            :disable="disableFieldsAlternative"
            :block-segments-update="disableFieldsAlternative"
            :campaign-id="campanha._id"
            hasCouponStyle
            :disable-segment-view="!campanha.has_segment_data || !campanha.status || includesThisStatus(['draft'])"
            :disable-segment-view-message="disableSegmentViewMessage"
            :campaign-created-at="campanha.created_at"
            :error="validations.campaign.segments.$error"
            :error-messages="'Selecione pelo menos um segmento'"
          />
        </div>
        <div class="div-auto-activation-step4" data-userflow-id="step4">
          <div class="div-flex-row-end">
            <div>
              <mf-tooltip v-if="!$getAllowedModules()['mobile']" top max-width="300px">
                <template #activator>
                  <div>
                    <mf-toggle v-model="campaign.isAutoActivated" hide-details :disabled="true" />
                  </div>
                </template>
                <span> Fale com o seu consultor para contratar o aplicativo e usar essa opção </span>
              </mf-tooltip>
              <mf-toggle v-else v-model="campaign.isAutoActivated" hide-details :disabled="disableFieldsAlternative" />
            </div>
            <span :class="['span-uso-imediato', { 'span-uso-imediato-disabled': disableFieldsAlternative || !$getAllowedModules()['mobile'] }]">
              Uso imediato do cupom (Próxima compra)
            </span>
          </div>
          <span class="span-uso-imediato-desc">
            Quando ativado, o cupom será enviado para a carteira dos clientes já ativado, permitindo o uso sem ações adicionais pelo aplicativo. Essa
            funcionalidade pode aument ar o volume de resgates e descontos aplicados.
          </span>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="div-nonsegmented-block">
        <div class="div-left-code">
          <span class="span-codigo-cupom">Código do cupom de desconto</span>
          <div class="div-flex-row-code">
            <v-text-field
              v-model="campaign.code"
              outlined
              maxlength="16"
              minlength="4"
              :success="computedValidCoupon"
              :success-messages="validCouponMessage"
              :error="computedValidCoupon === false || (validations.campaign.code && validations.campaign.code.$error)"
              :error-messages="invalidCouponMessage"
              :loading="loading"
              :disabled="loading || disableFields"
              class="v-text-field-maxwidth298"
              @blur="validateBenefitCode"
              @keypress="blockInvalidChar"
              @input="codeInput"
            >
              <template v-if="shouldShowAppend" #append>
                <mf-icon fill size="20" :icon="computedValidCoupon ? 'check_circle' : 'error'" :color="computedValidCoupon ? 'verde-0' : 'vermelho-0'" />
              </template>
            </v-text-field>
            <mf-button
              :loading="loading"
              text-transform="none"
              outlined
              color="marca-0-l20"
              label="Gerar código aleatório"
              :disabled="disableFields"
              class="mf-button-generate-code"
              @click="generateBenefitCode"
            />
          </div>
          <span :class="['span-code-info', { 'span-code-info-top0': validCouponMessage || invalidCouponMessage }]">
            Permitido somente letras maiúsculas e números. Nenhum ponto ou caractere especial é aceito.
          </span>
        </div>
        <div class="div-right-limit">
          <span class="span-limite-title">Limite de cupons de desconto</span>
          <mf-number-input
            v-model="campaign.limitUse"
            placeholder="6000"
            outlined
            :value="campaign.limitUse"
            :min="editableLimit || 0"
            show-arrows
            class="mf-number-input-width298"
            :disabled="disableFields"
            :error="validations.campaign.limitUse && validations.campaign.limitUse.$error"
            :error-messages="errorLimitUse"
            auto-format
          />
          <span :class="['span-limit-info', { 'span-limit-info-top0': validations.campaign.limitUse && validations.campaign.limitUse.$error }]">
            Quantidade máxima de cupons que podem ser cadastrados.
          </span>
        </div>
      </div>
    </template>

    <hr class="hr-separator" />

    <template>
      <div class="div-flex-align-top">
        <div class="div-half-block">
          <span class="span-dates-title">Datas</span>
          <div class="div-flex-row-dates">
            <div class="div-flex-date">
              <span class="span-label-date">Data de início</span>
              <date-picker
                :min="DateTime.now().toISODate()"
                :date.sync="campaign.startDate"
                :disabled="disableFieldsAlternative"
                :error-messages="errorStartDate"
                type="date"
                dense
                outlined
              />
            </div>
            <div>
              <hr class="hr-vertical-dates" />
            </div>
            <div class="div-flex-date-end">
              <span class="span-label-date">Data de expiração dos cupons</span>
              <date-picker
                :min="DateTime.now().toISODate()"
                :date.sync="campaign.expiresAt"
                :disabled="disableFieldsAlternative"
                :error-messages="errorEndDate"
                type="date"
                dense
                outlined
              />
            </div>
          </div>
        </div>

        <div v-if="!segmented" class="div-validade-variavel">
          <mf-toggle v-model="campaign.variableValidity" :disabled="disableFieldsAlternative" label="Validade variável" hide-details />
          <span class="span-validade-variavel-desc">
            Ative esta opção para que a validade do cupom de desconto seja variável, expirando X dias após ser adicionado à carteira.
          </span>
          <div v-if="campaign.variableValidity" class="div-validade-days">
            <mf-number-input
              v-model="campaign.daysToUseBenefitAfterRegistration"
              outlined
              dense
              label="Validade em dias"
              :min="1"
              show-arrows
              :disabled="disableFieldsAlternative"
              :error="hasDaysError"
              :error-messages="errorDays"
            />
          </div>
        </div>
      </div>
    </template>
  </mf-card-container>
</template>

<script>
import { DateTime } from 'luxon'
import { QUERY_GENERATE_BENEFIT_CODE, QUERY_VALIDATE_BENEFIT_CODE } from '../../../graphql'
import DatePicker from '@/components/DatePicker.vue'

export default {
  name: 'CouponDiscountSegmentToggle',
  components: {
    PublicoAlvo: () => import('@/modules/campanhas/views/components/PublicoAlvo.vue'),
    DatePicker,
  },
  props: {
    campaign: {
      type: Object,
      default: () => ({
        segments: [],
        has_segment_data: false,
        status: '',
        _id: '',
        created_at: '',
      }),
    },
    validations: {
      type: Object,
      default: () => ({}),
    },
    validCoupon: {
      type: Boolean,
      default: null,
    },
    segmented: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localSegments: (this.campaign.segmentsData || []).map((s) => ({
        ...s,
        clients_qty: s.clientsQty,
        clients_refreshed_at: s.clientsRefreshedAt,
      })),
      DateTime,
      editableLimit: null,
    }
  },
  computed: {
    campanha() {
      return (
        this.campaign || {
          segments: this.campaign.segments ?? [],
          has_segment_data: false,
          status: '',
          _id: '',
          created_at: '',
        }
      )
    },
    shouldShowAppend() {
      return this.computedValidCoupon != null || (this.validations.campaign.code && this.validations.campaign.code.$error)
    },

    segmentedClass() {
      const shouldDim = this.disableFieldsAlternative && !this.segmented
      let classes = ['segmented-base']
      if (this.segmented) {
        classes.push('segmented-active')
      } else {
        classes.push('segmented-inactive')
      }
      if (this.disableFieldsAlternative) {
        classes.push('segmented-disabled-cursor')
      }
      if (shouldDim) {
        classes.push('segmented-dim')
      }
      return classes
    },

    nonSegmentedClass() {
      const isMobileAllowed = this.$getAllowedModules()['mobile']
      const shouldDim = this.disableFieldsAlternative && this.segmented
      let classes = ['nonsegmented-base']
      if (!this.segmented) {
        classes.push('nonsegmented-active')
      } else {
        classes.push('nonsegmented-inactive')
      }
      if (!isMobileAllowed) {
        classes.push('nonsegmented-no-mobile')
      }
      if (!isMobileAllowed || this.disableFieldsAlternative) {
        classes.push('nonsegmented-disabled-cursor')
      }
      if (shouldDim) {
        classes.push('nonsegmented-dim')
      }
      return classes
    },

    isFinished() {
      return this.campaign.status === 'FINISHED'
    },
    isActive() {
      return this.campaign.status === 'ACTIVE'
    },
    disableFields() {
      return this.isFinished || this.isActive
    },
    disableFieldsAlternative() {
      return ['FINISHED', 'ACTIVE', 'SCHEDULED'].includes(this.campaign.status)
    },
    hasDateError() {
      return this.validations.campaign.startDate?.$error || this.validations.campaign.expiresAt?.$error
    },
    expirationInDaysComputed: {
      get() {
        return this.campaign.expiresAt
      },
      set(value) {
        this.campaign.expiresAt = value
      },
    },
    startDateComputed: {
      get() {
        return this.campaign.startDate
      },
      set(value) {
        this.campaign.startDate = value
      },
    },
    errorStartDate() {
      if (!this.validations.campaign || !this.validations.campaign.startDate) return ''
      if (this.validations.campaign.startDate.$error) {
        if (!this.validations.campaign.startDate.required) return 'Selecione uma data de início.'
        else if (this.campaign.status !== 'active' && !this.validations.campaign.startDate.isFuture) {
          return 'A data de validade deve ser no futuro.'
        }
      }
      return ''
    },
    errorEndDate() {
      if (!this.validations.campaign || !this.validations.campaign.expiresAt) return ''
      if (this.validations.campaign.expiresAt.$error) {
        if (!this.validations.campaign.expiresAt.required) return 'Selecione uma data de fim.'
        else if (this.campaign.status !== 'active' && !this.validations.campaign.expiresAt.isFuture) {
          return 'A data de validade deve ser no futuro.'
        } else if (!this.validations.campaign.expiresAt.endDateBiggerOrEqualThanStart) {
          return 'A data de expiração deve ser maior ou igual que a de início.'
        }
      }
      return ''
    },
    hasDaysError() {
      return this.validations.campaign.daysToUseBenefitAfterRegistration && this.validations.campaign.daysToUseBenefitAfterRegistration?.$error
    },
    errorDays() {
      return this.getErrorMessage('daysToUseBenefitAfterRegistration', {
        required: 'Informe a validade em dias.',
        minValue: 'Validade não pode ser menor do que 1.',
        maxValue: 'Validade não pode ser maior do que 30.',
      })
    },
    disableSegmentViewMessage() {
      return 'Não é possível visualizar critérios desse segmento'
    },
    computedValidCoupon: {
      get() {
        return this.validCoupon
      },
      set(value) {
        this.$emit('update:validCoupon', value)
      },
    },
    validCouponMessage() {
      return this.computedValidCoupon ? 'Código válido.' : ''
    },
    errorLimitUse() {
      if (!this.validations.campaign.limitUse) return ''
      if (this.validations.campaign.limitUse.$error) return 'Informe o limite de cupons de desconto.'
      return ''
    },
    invalidCouponMessage() {
      if (this.computedValidCoupon === false) {
        if (this.campaign.code.length > 16 || this.campaign.code.length < 4) {
          return 'Código inválido, número de caracteres inválido.'
        }
        return 'Código inválido, já utilizado em outra campanha.'
      }
      if (!this.validations.campaign.code) return ''
      if (this.validations.campaign.code.$error) {
        if (!this.validations.campaign.code.required) return 'Insira um código do cupom de desconto.'
        else if (!this.validations.campaign.code.minLength || !this.validations.campaign.code.maxLength)
          return 'Código inválido, número de caracteres inválido.'
      }
      return ''
    },
  },
  watch: {
    localSegments: {
      handler(newSegments) {
        if (Array.isArray(newSegments) && newSegments.length > 0 && newSegments[0]._id) {
          if (!Array.isArray(this.campaign.segments)) {
            this.$set(this.campaign, 'segments', [])
          }
          this.$set(this.campaign.segments, 0, newSegments[0]._id)
        }
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    if (this.campaign.limitUse && this.campaign.status !== 'DRAFT') this.editableLimit = this.campaign.limitUse

    if (!['ACTIVE', 'SCHEDULED'].includes(this.campaign.status)) {
      this.validateBenefitCode()
    }
  },
  methods: {
    includesThisStatus(statusList) {
      return statusList.includes(this.campanha.status)
    },
    handleNonSegmentedClick() {
      const isMobileAllowed = this.$getAllowedModules()['mobile']
      if (!this.disableFieldsAlternative && isMobileAllowed) {
        this.$emit('update:segmented', false)
      }
    },
    blockInvalidChar(event) {
      const allowedCharacters = /^[0-9a-zA-Z]*$/
      if (!allowedCharacters.test(event.key)) {
        event.preventDefault()
      }
    },
    codeInput() {
      this.campaign.code = this.campaign.code.toUpperCase()
      this.computedValidCoupon = null
    },
    async validateBenefitCode() {
      if (!this.campaign.code) return
      if (this.campaign.code.length < 4) {
        this.computedValidCoupon = false
        return
      }
      if (this.computedValidCoupon) return
      try {
        this.$emit('loading', true)
        const {
          data: { validateBenefitCode },
        } = await this.$apollo.mutate({
          mutation: QUERY_VALIDATE_BENEFIT_CODE,
          fetchPolicy: 'no-cache',
          variables: {
            input: {
              code: this.campaign.code,
              type: 'DISCOUNT_COUPON_ABSOLUTE',
            },
          },
          context: {
            uri: this.$microservicesUrls['benefitsManager'],
          },
        })
        this.computedValidCoupon = validateBenefitCode
      } catch (error) {
        this.computedValidCoupon = false
      } finally {
        this.$emit('loading', false)
      }
    },
    async generateBenefitCode() {
      try {
        this.computedValidCoupon = null
        this.$emit('loading', true)
        const {
          data: { generateRandomBenefitCode },
        } = await this.$apollo.mutate({
          mutation: QUERY_GENERATE_BENEFIT_CODE,
          fetchPolicy: 'no-cache',
          context: {
            uri: this.$microservicesUrls['benefitsManager'],
          },
        })
        this.campaign.code = generateRandomBenefitCode
        this.computedValidCoupon = true
      } catch (error) {
        this.$snackbar({ message: 'Erro ao gerar código do cupom', snackbarColor: 'error' })
      } finally {
        this.$emit('loading', false)
      }
    },
    getErrorMessage(type, errors) {
      if (this.isActive) return ''
      const validation = this.validations.campaign[type]
      if (!validation) return ''
      const errorKey = Object.keys(errors).find((key) => !validation[key])
      return errorKey ? errors[errorKey] : ''
    },
  },
}
</script>

<style lang="scss" scoped>
.mf-card-container-margin-top-24 {
  margin-top: 24px;
}

.span-title-modelo-aplicacao {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: #4b525a;
  margin-bottom: 20px;
}

.div-flex-space-between {
  display: flex;
  justify-content: space-between;
}

.segmented-base {
  flex: 1;
  margin-top: 12px;
  padding: 12px 20px;
  border-style: solid;
  border-radius: 14px 0 0 14px;
  cursor: pointer;
}

.segmented-active {
  border-width: 2px !important;
  border-color: #0063c6 !important;
  background-color: #d2e9ff33 !important;
}

.segmented-inactive {
  border-width: 1px !important;
  border-color: #6b7580 !important;
  background-color: #fff !important;
}

.segmented-disabled-cursor {
  cursor: default !important;
}

.segmented-dim {
  opacity: 0.5 !important;
}

.nonsegmented-base {
  flex: 1;
  margin-top: 12px;
  padding: 12px 20px;
  border-style: solid;
  border-radius: 0 14px 14px 0;
}

.nonsegmented-active {
  border-width: 2px !important;
  border-color: #0063c6 !important;
  background-color: #d2e9ff33 !important;
}

.nonsegmented-inactive {
  border-width: 1px !important;
  border-color: #6b7580 !important;
  background-color: #fff !important;
}

.nonsegmented-no-mobile {
  background-color: #f6f6f8 !important;
}

.nonsegmented-disabled-cursor {
  cursor: default !important;
}

.nonsegmented-dim {
  opacity: 0.5 !important;
}

.div-flex-row-mb-empty {
  display: flex;
  flex-direction: row;
  margin-bottom: 0px;
}

.span-com-segmento-title {
  display: block;
  font-size: 18px;
  font-weight: 400;
  color: #4b525a;
  margin-left: 8px;
}

.span-com-segmento-desc {
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: #4b525a;
}

.div-flex-row {
  display: flex;
  flex-direction: row;
}

.span-sem-segmento-title {
  display: block;
  font-size: 18px;
  font-weight: 400;
  color: #4b525a;
  margin-left: 8px;
}

.span-sem-segmento-desc {
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: #4b525a;
}

.div-flex-row-lock {
  display: flex;
  flex-direction: row;
  margin-top: 12px;
  align-items: center;
}

.span-lock-info {
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: #4b525a;
  margin-left: 8px;
}

.div-segmented-block {
  display: flex;
  margin-top: 32px;
  margin-bottom: 12px;
  flex-direction: row;
}

.div-publico-alvo-step3 {
  flex: 1;
}

.span-publico-alvo-title {
  display: block;
  font-size: 16px;
  font-weight: 400;
  color: #31363b;
}

.div-auto-activation-step4 {
  flex: 1;
  margin-left: 14px;
}

.div-flex-row-end {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
}

.span-uso-imediato {
  color: #4b525a;
  font-size: 16px;
  font-weight: 400;
  opacity: 1;
}

.span-uso-imediato-disabled {
  opacity: 0.5 !important;
}

.span-uso-imediato-desc {
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: #6b7580;
  margin-top: 16px;
}

.div-nonsegmented-block {
  display: flex;
  flex: 1;
  flex-direction: row;
  margin-top: 32px;
}

.div-left-code {
  flex: 1;
}

.span-codigo-cupom {
  display: block;
  font-size: 16px;
  font-weight: 400;
  color: #31363b;
  margin-bottom: 8px;
}

.div-flex-row-code {
  display: flex;
  flex-direction: row;
}

.v-text-field-maxwidth298 {
  max-width: 298px !important;
  min-width: 200px;
}

.mf-button-generate-code {
  margin-top: 10px !important;
  margin-left: 20px !important;
}

.span-code-info {
  display: block;
  font-size: 10px;
  font-weight: 400;
  color: #31363b;
  margin-bottom: 20px;
  margin-top: -20px;
}

.span-code-info-top0 {
  margin-top: 0px !important;
}

.div-right-limit {
  flex: 1;
}

.span-limite-title {
  display: block;
  font-size: 16px;
  font-weight: 400;
  color: #31363b;
  margin-bottom: 8px;
}

.mf-number-input-width298 {
  width: 298px !important;
}

.span-limit-info {
  display: block;
  font-size: 10px;
  font-weight: 400;
  color: #31363b;
  margin-bottom: 20px;
  margin-top: -20px;
}

.span-limit-info-top0 {
  margin-top: 0px !important;
}

.hr-separator {
  border: none;
  border-top: 1px solid #c3c8cd;
  margin-bottom: 20px;
}

.div-flex-align-top {
  display: flex;
  align-items: flex-start;
}

.div-half-block {
  flex: 1;
  max-width: 50%;
}

.span-dates-title {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: #4b525a;
  margin-bottom: 34px;
}

.div-flex-row-dates {
  display: flex;
  flex-direction: row;
}

.div-flex-date {
  flex: 1;
}

.span-label-date {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #4b525a;
  margin-bottom: 8px;
}

.hr-vertical-dates {
  height: 70%;
  border-left: 1px solid #6b7580;
  margin: 6px 20px;
}

.div-flex-date-end {
  flex: 1;
  margin-right: 70px;
}

.div-validade-variavel {
  flex: 1;
  margin-left: 14px;
  justify-content: space-between;
}

.span-validade-variavel-desc {
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: #6b7580;
  margin: 8px 0px;
  max-width: 371px;
}

.div-validade-days {
  max-width: 200px;
  margin-top: 16px;
}

@media (max-width: 1115px) {
  .div-flex-row-dates {
    flex-direction: column;
    align-items: flex-start;
  }

  .hr-vertical-dates {
    display: none;
  }

  .div-flex-date,
  .div-flex-date-end {
    width: 50%;
  }

  .div-flex-date-end {
    margin-right: 0;
    margin-top: 16px;
  }
}

@media (max-width: 1160px) {
  .div-nonsegmented-block {
    flex-direction: column;
    align-items: flex-start;
  }

  .div-left-code {
    width: 50%;
    min-width: 30%;
  }

  .div-right-limit {
    width: 50%;
  }
}

@media (max-width: 700px) {
  .div-flex-align-top {
    flex-direction: column;
    align-items: flex-start;
  }

  .div-half-block,
  .div-validade-variavel {
    width: 100%;
    max-width: 100%;
  }

  .div-flex-row-dates {
    width: 75%;
  }

  .div-flex-date,
  .div-flex-date-end {
    width: 100%;
    margin-right: 0;
    margin-top: 0;
  }

  .hr-vertical-dates {
    display: none;
  }
}
</style>
